import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  TotpVerifyRequestSchema,
  type TotpVerifyRequest,
} from '@truecare/schemas';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { ApiError } from '@truecare/api-client';
import { api } from '../api';
import { PortalShell } from '../components/PortalShell';
import {
  resolveDefaultPostSignInDestination,
  resolveNextRedirect,
} from '../lib/next-redirect';

/**
 * Phase 14b-3 audit C2 — TOTP-verification page for operators whose
 * first-factor sign-in (password OR magic-link) succeeded but whose
 * session still needs the second factor.
 *
 * Why this page is necessary: `OperatorGuard` (Phase 4) requires
 * `session.totpSatisfiedAt` to be non-null. Until this commit there
 * was no portal UI that called `/api/v1/auth/totp/verify` post-sign-
 * in; operators landed in admin → got 403 `totp_verification_required`
 * → had no UI affordance to recover. This page closes that gap.
 *
 * Flow:
 *   1. Password sign-in / magic-link verify completes → session created
 *   2. If user has `totpEnrolled = true`, sign-in handler navigates here
 *      with `?next=<original destination>` preserved
 *   3. Operator enters 6-digit code; POST /api/v1/auth/totp/verify
 *      sets `session.totpSatisfiedAt`
 *   4. On success, redirect to the resolved next URL OR persona default
 *
 * If user is not TOTP-enrolled (e.g., a caregiver/buyer who hasn't
 * gone through 14a enrolment yet), the sign-in handler skips this page
 * entirely — they don't need TOTP to access their own /me surface.
 */

const SearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute('/sign-in_/totp')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: TotpVerifyPage,
});

function TotpVerifyPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<TotpVerifyRequest>({
    resolver: zodResolver(TotpVerifyRequestSchema),
    defaultValues: { code: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: TotpVerifyRequest) => {
      const { error: respErr } = await api.POST(
        '/api/v1/auth/totp/verify',
        { body: input },
      );
      if (respErr) throw new ApiError('TOTP verify failed', 0, respErr);
    },
    onSuccess: async () => {
      // Same `next` allowlist resolution + persona-default fallback
      // as the password + magic-link sign-in handlers (consistency
      // matters — the same destination logic across all auth paths).
      //
      // Phase 14 audit fix (2026-06-25) — fetch /me FIRST, then seed
      // the auth-me query cache so PortalShell's header reflects the
      // newly-TOTP-satisfied state if we end up navigating same-origin.
      // Without this, the 30-second staleTime keeps useCurrentUser
      // returning the prior null until the user manually refreshes.
      const meResp = await api.GET('/api/v1/auth/me', {});
      if (meResp.data) {
        queryClient.setQueryData(['auth', 'me'], meResp.data);
      }
      const target = resolveNextRedirect(next ?? null);
      if (target) {
        window.location.href = target;
        return;
      }
      const dest = resolveDefaultPostSignInDestination(
        meResp.data?.roles ?? [],
      );
      if (dest.startsWith('http')) {
        window.location.href = dest;
      } else {
        void navigate({ to: dest });
      }
    },
    onError: () =>
      setError('That code is invalid or expired. Try the next one.'),
  });

  return (
    <PortalShell signedOutSurface>
      <div className="mx-auto max-w-md px-6 pt-16 pb-16">
        <Card padding="lg">
          <CardHeader
            title="Enter your authenticator code"
            description="Open your authenticator app and enter the 6-digit code shown for TrueCare™."
          />
          <form
            onSubmit={form.handleSubmit((data) => {
              setError(null);
              mutation.mutate(data);
            })}
            className="flex flex-col gap-4"
            noValidate
          >
            {error && <Alert tone="danger">{error}</Alert>}
            <Field
              label="6-digit code"
              error={form.formState.errors.code?.message}
            >
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="123456"
                autoFocus
                data-testid="totp-code"
                {...form.register('code')}
              />
            </Field>
            <Button
              type="submit"
              loading={mutation.isPending}
              data-testid="totp-submit"
            >
              Verify
            </Button>
          </form>
        </Card>
      </div>
    </PortalShell>
  );
}
